import { useTrackImpressionEventByIntersection } from "@product/scmp-sdk";
import type { FunctionComponent } from "react";
import { useCallback } from "react";

import type { PlusWidgetsSampleContentType } from "~/components/plus/plus-widget";
import { sendGA4Tracking } from "~/components/tracking/google-analytics-4/apis";
import type { ContentDiscoveryWidgetImpressionEvent } from "~/components/tracking/google-analytics-4/types";
import { useScmpWidgetConfig } from "~/lib/app/hooks";

import {
  Container,
  Intro,
  IntroText,
  SampleContent,
  SampleContentButtonLink,
  SampleContentHeadline,
  SampleContentImage,
  SampleContentItem,
  SampleContentItems,
  SampleContentType,
  StyledLogoPlus,
  StyledPlusWidgetQuotes,
  Title,
  TryForFreeButtonLink,
} from "./styles";

type Props = {
  className?: string;
  sampleContent: PlusWidgetsSampleContentType;
};
export const PlusWidgetWithoutAccess: FunctionComponent<Props> = ({ className, sampleContent }) => {
  const { scmpWidgetConfigState } = useScmpWidgetConfig();
  const discoveryModuleSampleContent = sampleContent
    ? scmpWidgetConfigState.result?.discovery_module_sample_content?.[sampleContent]
    : null;

  const ga4ImpressionEvent = useCallback<() => ContentDiscoveryWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "subs",
      customized_parameters: {
        call_to_action: "plus trial",
        trigger_point: "content_discovery",
      },
      subcategory: "entry_point",
    }),
    [],
  );

  const { captureTrackImpressionEventTargetElement } = useTrackImpressionEventByIntersection<
    null,
    ContentDiscoveryWidgetImpressionEvent
  >({
    ga4TrackingHandler: sendGA4Tracking,
    getGa4Event: ga4ImpressionEvent,
    options: {
      isSendGA4Tracking: true,
      shouldSendOnce: true,
    },
  });

  const handleTrialButtonClickEvent = () => {
    sendGA4Tracking({
      action: "click",
      category: "subs",
      customized_parameters: {
        call_to_action: "plus trial",
        trigger_point: "content_discovery",
      },
      subcategory: "entry_point",
    });
  };

  const handleDownloadClickEvent = () => {
    sendGA4Tracking({
      action: "click",
      category: "plus",
      customized_parameters: {
        action_type: "download",
        trigger_point: "content_discovery",
      },
      subcategory: "sample",
    });
  };

  return (
    <Container className={className} ref={captureTrackImpressionEventTargetElement}>
      <StyledLogoPlus />
      <Intro>
        <IntroText>
          SCMP Plus is a new premium news platform that gives you an{" "}
          <span>all-inclusive edge to stay ahead on China news.</span> To access our exclusive
          content you’ll need to subscribe.
        </IntroText>
        <TryForFreeButtonLink
          pathname="https://subscribe.scmp.com/plus"
          onClick={handleTrialButtonClickEvent}
        >
          TRY FOR FREE
        </TryForFreeButtonLink>
      </Intro>
      <StyledPlusWidgetQuotes />
      <SampleContent>
        <Title>SNEAK PEEK OF SCMP PLUS</Title>
        <SampleContentItems>
          {discoveryModuleSampleContent?.map((item, index) => (
            <SampleContentItem key={index}>
              <SampleContentType>{item.type}</SampleContentType>
              <SampleContentHeadline>{item.headline}</SampleContentHeadline>
              {item.downloadLink && (
                <SampleContentButtonLink
                  anchorProps={{ target: "_blank" }}
                  pathname={item.downloadLink}
                  onClick={handleDownloadClickEvent}
                >
                  DOWNLOAD SAMPLE
                </SampleContentButtonLink>
              )}
              <SampleContentImage alt={item.headline} src={item.image} />
            </SampleContentItem>
          ))}
        </SampleContentItems>
      </SampleContent>
    </Container>
  );
};

PlusWidgetWithoutAccess.displayName = "PlusWidgetWithoutAccess";
